/**
 * ConfirmationStep - Shows address summary for user confirmation
 */

interface ConfirmationStepProps {
  fullName: string
  addressLine1: string
  addressLine2?: string
  city: string
  state: string
  zipCode: string
  normalized?: {
    address_line1: string
    city: string
    state: string
    zip_code: string
  } | null
  validationNote?: string | null
  onConfirm: () => void
  onGoBack: () => void
  isSubmitting: boolean
}

export function ConfirmationStep({
  fullName,
  addressLine1,
  addressLine2,
  city,
  state,
  zipCode,
  normalized,
  validationNote,
  onConfirm,
  onGoBack,
  isSubmitting
}: ConfirmationStepProps) {
  // Use normalized address if available, otherwise use what the user typed
  const displayAddress = normalized ? {
    line1: normalized.address_line1,
    city: normalized.city,
    state: normalized.state,
    zip: normalized.zip_code
  } : {
    line1: addressLine1,
    city,
    state,
    zip: zipCode
  }

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Confirm Your Address</h3>
        <p className="text-sm text-gray-500 mt-1">Please make sure everything looks correct</p>
      </div>

      {/* Address Card */}
      <div className="bg-[#f6f3ee] border border-[#ece6dd] rounded-lg p-4">
        <p className="font-medium text-gray-900">{fullName}</p>
        <p className="text-sm text-gray-700 mt-1">{displayAddress.line1}</p>
        {addressLine2 && <p className="text-sm text-gray-700">{addressLine2}</p>}
        <p className="text-sm text-gray-700">{displayAddress.city}, {displayAddress.state} {displayAddress.zip}</p>
      </div>

      {/* Normalized address notice */}
      {normalized && (
        <p className="text-xs text-gray-500 text-center">
          Address has been standardized by USPS verification.
        </p>
      )}

      {/* Validation note */}
      {validationNote && (
        <p className="text-xs text-amber-600 text-center bg-amber-50 border border-amber-200 rounded-lg p-2">
          {validationNote}
        </p>
      )}

      {/* Buttons */}
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onGoBack}
          className="flex-1 py-2 px-4 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          disabled={isSubmitting}
        >
          Go Back
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="flex-1 py-2 px-4 text-sm font-medium text-white bg-[#6b7850] rounded-lg hover:bg-[#5c5b44] transition-colors disabled:opacity-50"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Sending...' : 'Accept & Send'}
        </button>
      </div>
    </div>
  )
}
